import { ChevronLeft, ChevronRight } from "lucide-react";
import { toISO, todayISO } from "@/lib/api";

const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const TONES = {
  teal: "bg-[#edf2ef] text-[#2f4f47] border-[#b8956f]",
  dark: "bg-slate-900 text-white border-slate-900",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200 line-through",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  violet: "bg-indigo-50 text-indigo-700 border-indigo-200",
};

export function monthDays(month) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(first);
  start.setDate(1 - ((first.getDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

export const monthRange = (month) => {
  const days = monthDays(month);
  return { start: toISO(days[0]), end: toISO(days[41]) };
};

export function MonthCalendar({ month, onMonthChange, events, selected, onSelectDay, onEventClick, prefix }) {
  const days = monthDays(month);
  const today = todayISO();
  const shift = (n) => onMonthChange(new Date(month.getFullYear(), month.getMonth() + n, 1));
  const byDate = events.reduce((acc, e) => ({ ...acc, [e.date]: [...(acc[e.date] || []), e] }), {});

  return (
    <div className="card p-4 sm:p-6" data-testid={`${prefix}-calendar`}>
      <div className="mb-4 flex items-center justify-between gap-2">
        <h2 className="text-lg font-bold capitalize sm:text-xl" data-testid={`${prefix}-calendar-month`}>
          {month.toLocaleDateString("it-IT", { month: "long", year: "numeric" })}
        </h2>
        <div className="flex items-center gap-1">
          <button className="btn-ghost" onClick={() => shift(-1)} data-testid={`${prefix}-calendar-prev`}><ChevronLeft className="h-4 w-4" /></button>
          <button className="btn-ghost text-xs font-semibold" onClick={() => { const n = new Date(); onMonthChange(new Date(n.getFullYear(), n.getMonth(), 1)); onSelectDay?.(today); }} data-testid={`${prefix}-calendar-today`}>Oggi</button>
          <button className="btn-ghost" onClick={() => shift(1)} data-testid={`${prefix}-calendar-next`}><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((w) => <div key={w} className="label-xs py-2">{w}</div>)}
        {days.map((d) => {
          const iso = toISO(d);
          const list = byDate[iso] || [];
          const inMonth = d.getMonth() === month.getMonth();
          const isSel = selected === iso;
          return (
            <button key={iso} onClick={() => onSelectDay?.(iso)} data-testid={`${prefix}-day-${iso}`}
              className={`min-h-[68px] rounded-xl border p-1.5 text-left transition-colors sm:min-h-[96px] ${
                isSel ? "border-[#3d655b] bg-[#edf2ef]/60" : "border-transparent hover:bg-slate-50"
              } ${inMonth ? "" : "opacity-40"}`}>
              <div className={`mb-1 inline-grid h-6 w-6 place-items-center rounded-full text-xs font-semibold ${
                iso === today ? "bg-[#3d655b] text-white" : "text-slate-700"}`}>{d.getDate()}</div>
              <div className="space-y-0.5">
                {list.slice(0, 3).map((e) => (
                  <div key={e.id} onClick={(ev) => { ev.stopPropagation(); onEventClick?.(e); }}
                    data-testid={`${prefix}-event-${e.id}`}
                    className={`truncate rounded-md border px-1.5 py-0.5 text-[10px] font-medium sm:text-[11px] ${TONES[e.tone || "teal"]}`}>
                    {e.time && <span className="hidden font-bold sm:inline">{e.time} </span>}{e.label}
                  </div>
                ))}
                {list.length > 3 && <div className="text-[10px] font-semibold text-slate-500">+{list.length - 3}</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
